// Story 8.2 (ADO-2011), AC6 — the search result card for a DISCONTINUED product that
// an exact product code / EAN / TUC search surfaced because it has a live substitute
// at the Shopper's depot (the server decides; `notAvailable: true` on the row).
//
// SL1.0 parity: an inactive product that is shown has its Add and quantity controls
// hidden (`get_smanager.php:3264-3313`, `cabinet_smanager.htm:2320-2325,2456-2479`),
// says "Product not currently available on shoplink." (`stock_and_subs.php:26-33`)
// and offers "See the substitute" (`cabinet_smanager.htm:2480-2483`). So this card
// renders the image, code, name, that copy and "See the substitute" (the shared
// `SubstitutesPanel`, loaded at the session depot), and NO Add, quantity stepper,
// stock band, favourite toggle or product-page link: the PDP and favourite routes read
// the live projection this product has left.
//
// The panel lists the substitutes as loaded (capped at the card's display cap), not
// filtered by stock: SL1.0's `lookForSubs` offers ACTIVE substitutes at the depot, and
// a supplier-unavailable one is still blocked at its own Add by the basket store.
// Pricing migration 17b (ADO-3948, DEC-3): the card prices them as soon as they load and leaves
// out one with no price of its own before the cap, so "See the substitute" stays pending while
// their prices arrive, and says "No substitutes available" when none survives.
//
// An off-sale RBP core parent with a child the caller can order (`rbpLinks: 'OFFERED'`, RBP-D33)
// offers "See RBP links" as its footer action, as SL1.0 shows the RBP button on an inactive item
// (`get_smanager.php:3276-3296`); "See the substitute" is offered only once a substitute has loaded,
// in its own row under the footer, so the card never shows a pending action that may vanish and
// the RBP button never moves.
//
// Layout follows the listing's grid/list view toggle (ADO-2011 comment 7281461): `grid`
// is the vertical card, `list` reuses the orderable card's list-row markup (image,
// name, badge row with the code, meta, footer, inline + overlay substitutes hosts)
// without the Add/quantity controls, with "See the substitute" in the CTA slot.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon } from '../../design-system';
import type { SearchResultProduct } from '../../api/search.client';
import { useSessionDepotId } from '../../availability/session-depot';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { atDepot } from '../../availability/depot-scoped-entry';
import { useSubstitutesStore } from '../../substitutes/useSubstitutesStore';
import { useFavouritesStore } from '../../favourites/useFavouritesStore';
import { useListedProducts } from '../../pricing/listVisibility';
import type { SubstituteProduct } from '../../api/substitutes.client';
import { ProductImage } from './ProductImage';
import { SubstitutesPanel } from './SubstitutesPanel';
import { RbpLinksButton } from './RbpLinksCta';
import styles from './ProductCard.module.css';

/** SL1.0's copy for a product it will not add (`www/engine/stock_and_subs.php:26-33`).
 *  Lock-step with the server's `PRODUCT_DISCONTINUED_MESSAGE`, guarded by
 *  `apps/api/__tests__/tenancy/harness/__tests__/basket-web-mirror-lockstep.spec.ts`. */
export const PRODUCT_NOT_AVAILABLE_MESSAGE = 'Product not currently available on shoplink.';

/** The substitutes shown at once (the card's `MAX_SUBSTITUTES_DISPLAYED`, repeated
 *  here to keep this module free of an import cycle with `ProductCard`). */
const MAX_SUBSTITUTES_SHOWN = 3;

/** The status a loaded-but-empty substitute list gives the CTA (the Story 7-FU-I AC4
 *  label). Reachable when stock or the depot changes between the search response and
 *  this card's own substitute fetch. */
export const NO_SUBSTITUTES_MESSAGE = 'No substitutes available';

const NO_SUBSTITUTES: readonly SubstituteProduct[] = [];
const substituteSku = (substitute: SubstituteProduct): string => substitute.sku;

export interface NotAvailableProductCardProps {
  readonly product: SearchResultProduct;
  readonly priority?: boolean;
  /** The listing's view mode (the `ProductCard` `layout` prop). */
  readonly layout?: 'grid' | 'list';
  /** No Linked Product upsell on a substitute's Add. Set by the scanner, whose sheet sits in
   *  its own fullscreen dialog (the confirm sheet's `upsell: false`, Story 7.18). */
  readonly suppressUpsell?: boolean;
  /** Story 18.11 review (ADO-3686): in the /favourites view a favourite that has gone off sale
   *  renders here (the orderable card's heart is gone), so keep a remove-favourite control on it
   *  or the shopper can no longer remove it. Off everywhere else: browse and search never offer a
   *  heart on a discontinued row. */
  readonly favouritesView?: boolean;
}

export function NotAvailableProductCard({
  product,
  priority = false,
  layout = 'grid',
  suppressUpsell = false,
  favouritesView = false,
}: NotAvailableProductCardProps) {
  const sessionDepotId = useSessionDepotId();
  // Enabler E7-C (ADO-2274), D-1: the substitutes are the selected entry's; an entry change
  // resets the store, and the effect below loads them again.
  const catalogueEntryKey = useCatalogueEntryKey();
  const load = useSubstitutesStore((s) => s.load);
  const retain = useSubstitutesStore((s) => s.retain);
  const release = useSubstitutesStore((s) => s.release);
  const rawEntry = useSubstitutesStore((s) => s.bySku[product.sku]);
  const entry = atDepot(rawEntry, sessionDepotId);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sku = product.sku;
    retain(sku);
    void load(sku);
    return () => release(sku);
    // `sessionDepotId`: the substitutes are depot-scoped, re-load on a live switch.
  }, [product.sku, load, retain, release, sessionDepotId, catalogueEntryKey]);

  // ADO-3948: hide before the cap, so a hidden substitute's place goes to the next one.
  const { shown: pricedSubstitutes, settling: substitutePricesSettling } = useListedProducts(
    entry?.phase === 'loaded' ? entry.substitutes : NO_SUBSTITUTES,
    { skuOf: substituteSku },
  );
  const substitutes = pricedSubstitutes.slice(0, MAX_SUBSTITUTES_SHOWN);
  const ready = substitutes.length > 0;
  const statusId = `not-available-status-${product.sku}`;
  // Three non-ready states, told apart: a failed load, a load that finished with no
  // substitute, and a load still pending (the same split as `ProductCard`'s
  // `substituteCtaTitle`), so a finished-empty load never reads "Loading…" forever.
  const status =
    entry?.phase === 'error'
      ? 'Substitutes could not be loaded'
      : ready
        ? undefined
        : entry?.phase === 'loaded' && !substitutePricesSettling
          ? NO_SUBSTITUTES_MESSAGE
          : 'Loading substitutes…';

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.querySelector('button')?.focus({ preventScroll: true });
  }, []);

  // The panel unmounts when `ready` turns false (a live stock / depot push emptying the
  // list while it is open). Close it then, so `aria-expanded` stops announcing a dialog
  // that is gone and focus returns to the trigger (the guard `OrderableProductCard`
  // carries for the same race).
  useEffect(() => {
    if (open && !ready) close();
  }, [open, ready, close]);

  const offersRbpLinks = product.rbpLinks === 'OFFERED';

  // Story 18.11 review (ADO-3686): the /favourites view routes an off-sale favourite here, where
  // the orderable card's heart is gone. Keep a remove-favourite control so the shopper can still
  // take it off the list. Off in browse and search (`favouritesView` false), which never offer a
  // heart on a discontinued row.
  const isFavourite = useFavouritesStore((state) => state.favouriteSkus.has(product.sku));
  const favouritePending = useFavouritesStore((state) => state.pending.has(product.sku));
  const addFavourite = useFavouritesStore((state) => state.add);
  const removeFavourite = useFavouritesStore((state) => state.remove);
  const favouriteHeart = () =>
    favouritesView ? (
      <button
        type="button"
        className={styles.heart}
        data-favourited={isFavourite}
        aria-pressed={isFavourite}
        disabled={favouritePending}
        aria-label={
          isFavourite
            ? `Remove ${product.name} from favourites`
            : `Add ${product.name} to favourites`
        }
        onClick={() => {
          if (isFavourite) void removeFavourite(product.sku);
          else void addFavourite(product.sku);
        }}
      >
        <Icon name="heart" size={22} />
      </button>
    ) : null;

  // With the RBP links as the card's action, a substitute is offered only once one has loaded.
  const substituteCta =
    offersRbpLinks && !ready ? null : (
      <>
        <Button
          variant="substitute"
          className={styles.notAvailableCta}
          onClick={ready ? () => setOpen(true) : undefined}
          aria-disabled={ready ? undefined : true}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-describedby={status ? statusId : undefined}
          aria-label={`See the substitute for ${product.name}`}
        >
          See the substitute
        </Button>
        {status ? (
          <span id={statusId} className={styles.visuallyHidden}>
            {status}
          </span>
        ) : null}
      </>
    );

  const footer = (
    <div className={styles.footer}>
      <span className={styles.priceSlot} aria-hidden="true" />
      <div className={styles.ctaCluster} ref={offersRbpLinks ? undefined : triggerRef}>
        {offersRbpLinks ? <RbpLinksButton sku={product.sku} name={product.name} /> : substituteCta}
      </div>
    </div>
  );

  // With the RBP links as the footer's action, a loaded substitute gets its own row under it.
  const substituteRow =
    offersRbpLinks && substituteCta !== null ? (
      <div
        className={
          layout === 'list' ? `${styles.rbpLinksRow} ${styles.listFullRow}` : styles.rbpLinksRow
        }
        ref={triggerRef}
      >
        {substituteCta}
      </div>
    ) : null;

  const overlayPanel = ready ? (
    <SubstitutesPanel
      productName={product.name}
      substitutes={substitutes}
      open={open}
      onClose={close}
      suppressUpsell={suppressUpsell}
      pricedByHost
    />
  ) : null;

  const listImage = (
    <div className={styles.imageBlock}>
      <ProductImage src={product.imageUrl} alt={product.name} priority={priority} />
    </div>
  );

  // List row: the orderable card's list markup (`ProductCard.tsx` list branch), with
  // no heart, stock pill, stock row or Add, and the substitutes in the same two
  // media-gated hosts (inline ≥768px, slide-up overlay <768px).
  if (layout === 'list') {
    return (
      <article className={styles.card} data-layout="list" data-not-available="true">
        {listImage}
        <h3 className={styles.listName}>{product.name}</h3>
        <div className={styles.listBadgeRow}>
          <span className={styles.sku}>{product.sku}</span>
          {favouriteHeart()}
        </div>
        <div className={styles.listMeta}>
          <p className={styles.notAvailableMessage}>{PRODUCT_NOT_AVAILABLE_MESSAGE}</p>
        </div>
        {footer}
        {substituteRow}
        {ready ? (
          <>
            <div className={`${styles.listFullRow} ${styles.subInlineHost}`}>
              <SubstitutesPanel
                productName={product.name}
                substitutes={substitutes}
                open={open}
                variant="inline"
                onClose={close}
                suppressUpsell={suppressUpsell}
                pricedByHost
              />
            </div>
            <div className={styles.subOverlayHost}>{overlayPanel}</div>
          </>
        ) : null}
      </article>
    );
  }

  return (
    <article className={styles.card} data-layout="grid" data-not-available="true">
      {/* The grid card's Figma image: full width, 145px tall (no chrome row: no heart). */}
      <div className={styles.imageBlock}>
        <div className={styles.imageFrame}>
          <ProductImage src={product.imageUrl} alt={product.name} priority={priority} />
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.headerRow}>
          <span className={styles.sku}>{product.sku}</span>
          {favouriteHeart()}
        </div>
        <h3 className={styles.name}>{product.name}</h3>
        <p className={styles.notAvailableMessage}>{PRODUCT_NOT_AVAILABLE_MESSAGE}</p>
      </div>
      {footer}
      {substituteRow}
      {overlayPanel}
    </article>
  );
}
